import { useEffect, useState, useCallback } from "react";
import {
  Bell,
  Sparkles,
  CalendarCheck,
  AlertTriangle,
  Bot,
  Info,
  Trash2,
  CheckCheck,
  BellOff,
  Loader2,
  ChevronDown,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import PageHeader from "@/components/PageHeader";
import { drApi, notificationsApi } from "@/lib/api";
import { useAuthStore } from "@/store/authStore";
import { useNavigate } from "react-router-dom";

const SECTIONS = [
  { key: "emergency", label: "Emergency & Risk Alerts", icon: AlertTriangle, color: "#DC2626" },
  { key: "booking", label: "Appointment Updates", icon: CalendarCheck, color: "#2563EB" },
  { key: "reminder", label: "Reminders", icon: Bell, color: "#F59E0B" },
  { key: "ai", label: "AI Health Updates", icon: Bot, color: "#7C3AED" },
  { key: "other", label: "Other", icon: Info, color: "#64748B" },
];

function categorize(n) {
  const t = (n.notification_type || "").toLowerCase();
  const dt = (n.data?.type || "").toLowerCase();
  const s = `${t} ${dt}`;
  // Emergency & risk alerts (pinned top)
  if (/(critical|warning|emergency|outbreak|risk_increase|anomaly|health_alert|healthguard|ambulance|sos|incoming_emergency)/.test(s) || dt === "zone_risk_alert") return "emergency";
  // Reminders (upcoming appointments + medications) — checked before booking.
  if (/(reminder|refill|vaccination|checkin|check_in|medication)/.test(s)) return "reminder";
  // Appointment updates — confirmations/cancellations/reschedules for doctor
  // consultations, lab tests and diagnostic tests (+ orders / at-home care).
  if (/(appointment|booking|consultation|teleconsult|lab_|pathology|diagnostic|medicine_order|order|at_home|pharmacy_chat|case_status|case_update|results_ready|report_ready)/.test(s)) return "booking";
  // AI health updates
  if (/(ehr|ai_|insight|dr_sanjeevani|sanjeevani|screening|prescription|health_update|risk_score)/.test(s) || n.data?.action === "view_ehr") return "ai";
  return "other";
}

function formatTime(dateString) {
  const date = new Date(dateString);
  const diffMins = Math.floor((Date.now() - date.getTime()) / 60000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

const WEB_SCREEN_MAP = {
  "/main/health-records": "/records",
  "/main/health-center": "/vital-map",
};

export default function Notifications() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [tip, setTip] = useState("");
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(() =>
    SECTIONS.reduce((acc, s) => ({ ...acc, [s.key]: true }), {})
  );

  const toggleSection = (key) => setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));

  const load = useCallback(() => {
    if (!user?._id) return;
    notificationsApi
      .list(user._id)
      .then((res) => setNotifications(res.data?.notifications || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user?._id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!user?._id) return;
    drApi.nudges(user._id).then((res) => setTip(res.data?.greeting || "")).catch(() => {});
  }, [user?._id]);

  const handlePress = (n) => {
    if (!n.is_read) {
      notificationsApi.markRead(n.id).catch(() => {});
      setNotifications((prev) => prev.map((x) => (x.id === n.id ? { ...x, is_read: true } : x)));
    }
    let target = n.data?.screen;
    if (n.notification_type === "ehr_shared" || n.data?.action === "view_ehr") target = "/main/health-records";
    else if (n.data?.type === "zone_risk_alert") target = "/main/health-center";
    if (target) navigate(WEB_SCREEN_MAP[target] || target);
  };

  const remove = (e, id) => {
    e.stopPropagation();
    notificationsApi.remove(id).catch(() => {});
    setNotifications((prev) => prev.filter((x) => x.id !== id));
  };

  const markAll = () => {
    if (!user?._id) return;
    notificationsApi.markAllRead(user._id).catch(() => {});
    setNotifications((prev) => prev.map((x) => ({ ...x, is_read: true })));
  };

  const unreadCount = notifications.filter((n) => !n.is_read).length;
  const grouped = { emergency: [], booking: [], ai: [], reminder: [], other: [] };
  notifications.forEach((n) => grouped[categorize(n)].push(n));

  return (
    <div className="max-w-3xl" data-testid="notifications-page">
      <PageHeader
        title="Notifications"
        subtitle="Updates & reminders"
        icon={Bell}
        color="#2563EB"
        right={
          unreadCount > 0 ? (
            <button
              onClick={markAll}
              className="text-sm font-semibold text-primary flex items-center gap-1"
              data-testid="notif-mark-all"
            >
              <CheckCheck className="h-4 w-4" /> Mark all read
            </button>
          ) : null
        }
      />

      {tip && (
        <Card className="rounded-2xl p-4 border-border bg-[#7C3AED]/5 flex items-start gap-3 mb-4" data-testid="notif-ai-tip">
          <div className="h-10 w-10 rounded-xl bg-[#7C3AED]/15 flex items-center justify-center shrink-0">
            <Sparkles className="h-5 w-5 text-[#7C3AED]" />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-sm">Dr Sanjeevani</p>
            <p className="text-sm text-muted-foreground mt-0.5">{tip}</p>
          </div>
          <button onClick={() => navigate("/assistant")} className="text-[#7C3AED] text-sm font-medium shrink-0" data-testid="notif-open-chat">Open</button>
        </Card>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : notifications.length === 0 ? (
        <Card className="rounded-2xl border-border flex flex-col items-center justify-center py-16 px-6 text-center" data-testid="notif-empty">
          <BellOff className="h-12 w-12 text-muted-foreground/40" />
          <p className="mt-4 font-semibold text-muted-foreground">You&apos;re all caught up</p>
          <p className="text-sm text-muted-foreground mt-1">New updates & reminders will appear here.</p>
        </Card>
      ) : (
        <div className="space-y-6">
          {SECTIONS.map((section) => {
            const items = grouped[section.key];
            if (!items.length) return null;
            const SIcon = section.icon;
            const sectionUnread = items.filter((n) => !n.is_read).length;
            const isCollapsed = !!collapsed[section.key];
            return (
              <div key={section.key} data-testid={`notif-section-${section.key}`}>
                <button
                  onClick={() => toggleSection(section.key)}
                  className="w-full flex items-center gap-2 mb-2 px-1"
                  data-testid={`notif-section-toggle-${section.key}`}
                >
                  <div className="h-6 w-6 rounded-lg flex items-center justify-center" style={{ backgroundColor: section.color + "1A" }}>
                    <SIcon className="h-3.5 w-3.5" style={{ color: section.color }} />
                  </div>
                  <h2 className="text-sm font-bold tracking-tight">{section.label}</h2>
                  {sectionUnread > 0 && (
                    <span className="text-[10px] font-bold text-white rounded-full px-1.5 min-w-[18px] text-center" style={{ backgroundColor: section.color }}>
                      {sectionUnread}
                    </span>
                  )}
                  <span className="flex-1" />
                  <span className="text-xs font-semibold text-muted-foreground">{items.length}</span>
                  <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${isCollapsed ? "" : "rotate-180"}`} />
                </button>
                {!isCollapsed && (
                  <Card className="rounded-2xl divide-y divide-border overflow-hidden border-border">
                    {items.map((n) => (
                      <button
                        key={n.id}
                        onClick={() => handlePress(n)}
                        className={`w-full flex items-start gap-3 p-4 hover:bg-accent transition-colors text-left relative ${!n.is_read ? "bg-primary/5" : ""}`}
                        data-testid={`notification-${n.id}`}
                      >
                        <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: section.color + "1A" }}>
                          <SIcon className="h-5 w-5" style={{ color: section.color }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm">{n.title}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{n.message}</p>
                          <p className="text-[11px] text-muted-foreground/70 mt-1.5">{formatTime(n.created_at)}</p>
                        </div>
                        {!n.is_read ? (
                          <span className="h-2.5 w-2.5 rounded-full bg-primary shrink-0 mt-1.5" />
                        ) : (
                          <span
                            onClick={(e) => remove(e, n.id)}
                            className="text-muted-foreground/60 hover:text-destructive shrink-0 p-1 cursor-pointer"
                            data-testid={`delete-notification-${n.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </span>
                        )}
                      </button>
                    ))}
                  </Card>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
